import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import SocialPreview from "../components/marketing/SocialPreview";
import SocialAnalytics from "../components/marketing/SocialAnalytics";
import BioManager from "../components/marketing/BioManager";
import Connections from "../components/marketing/Connections";
import GrowthAds from "../components/growth/GrowthAds";
import ActivityFeed from "../components/marketing/ActivityFeed";
import ContentStudio from "../components/marketing/ContentStudio";
import MultiPlatform from "../components/marketing/MultiPlatform";
import ContentCalendar from "../components/marketing/ContentCalendar";
import LaunchCampaigns from "../components/marketing/LaunchCampaigns";
import AiAssist from "../components/AiAssist";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Sparkles, PenLine, Share2, Mail, Radar, Palette, Check, X, Trash2,
  Copy, Send, Zap, TrendingUp, UserPlus, Loader2, Megaphone, Image as ImageIcon, CalendarClock, CalendarDays, Layers, Grid3x3, Facebook, Instagram, Linkedin, BarChart3, Activity, Rocket, Target as TargetIcon, Link2, Clock,
} from "lucide-react";
import {
  getKit, updateKit, generateContent, getDrafts, setDraftStatus, deleteDraft, publishDraft,
  getCampaigns, createCampaign, getProspects, enrichProspect, convertProspect,
  createAd, getAds, setAdStatus, getMedia, addMedia, schedulePost, getScheduledPosts,
  downloadProspectLetter, exportProspectsCsv,
  setProspectStage, getProspectRoi, getProspectCampaigns, createProspectCampaign, enrolProspects, generateProspects,
  fetchProperties, scheduleDraft, publishBlogFromDraft,
} from "../api";

const input = "w-full h-[46px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]";
const area = "w-full px-4 py-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] resize-none";
const btn = "inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60";

const DraftCard = ({ d, onStatus, onDelete, onCopy, onPublish, onSchedule, onPublishBlog }) => {
  const color = { pending: "bg-amber-100 text-amber-700", approved: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-600", published: "bg-blue-100 text-blue-700" };
  const social = ["post", "caption", "ad", "carousel", "reel", "story"].includes(d.type);
  const canPublish = social && d.status !== "published" && d.status !== "rejected" && onPublish;
  const hasImg = !!(d.image || (d.images && d.images.length));
  const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-[12px] hover:opacity-90 transition-opacity";
  const fbBtn = <button key="fb" onClick={() => onPublish(d, "facebook")} data-testid="publish-facebook-btn" className={`${btn} bg-[#1877F2]`}><Facebook size={13} /> Post to Facebook</button>;
  const igBtn = <button key="ig" onClick={() => onPublish(d, "instagram")} data-testid="publish-instagram-btn" className={`${btn} bg-gradient-to-r from-[#E1306C] to-[#F77737]`}><Instagram size={13} /> {d.type === "carousel" ? "Post Carousel" : "Post to Instagram"}</button>;
  const bothBtn = <button key="both" onClick={() => onPublish(d, "both")} data-testid="publish-both-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1c1c1e] text-white text-[12px] hover:bg-black transition-colors"><Zap size={13} /> Post to Both</button>;
  const liBtn = <button key="li" onClick={() => onPublish(d, "linkedin")} data-testid="publish-linkedin-btn" className={`${btn} bg-[#0A66C2]`}><Linkedin size={13} /> Post to LinkedIn</button>;
  const igHint = <span key="ighint" className="text-[11px] text-[#a0a0a0]">Add an image to post to Instagram</span>;
  const renderPublish = () => {
    const p = d.platform;
    if (p === "facebook") return [fbBtn];
    if (p === "instagram") return [hasImg ? igBtn : igHint];
    if (p === "linkedin") return [liBtn];
    if (p === "tiktok") return [<span key="tt" className="text-[11px] text-[#a0a0a0]">TikTok isn't connected — copy the script & post it in the app</span>];
    return [fbBtn, hasImg && igBtn, hasImg && bothBtn, liBtn].filter(Boolean);
  };
  return (
    <div className="bg-white rounded-xl border border-[#f0ece5] p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] uppercase tracking-wide text-[#b8935f] font-500">{d.type}{d.platform ? ` · ${d.platform}` : ""}</span>
        <span className={`text-[11px] px-2.5 py-1 rounded-full capitalize ${color[d.status]}`}>{d.status === "pending" ? "Pending Review" : d.status}</span>
      </div>
      <p className="text-[14px] text-[#3a3a3a] whitespace-pre-wrap leading-relaxed mb-4 max-h-56 overflow-y-auto">{d.content}</p>
      {d.type === "carousel" && (d.images || []).length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto pb-2 mb-3" data-testid="draft-carousel-strip">
          {(d.images || []).map((img, i) => (
            <img key={i} src={img} alt={`slide ${i + 1}`} className="h-20 w-20 object-cover rounded-lg border border-[#f0ece5] shrink-0" />
          ))}
          <span className="shrink-0 self-center text-[11px] text-[#a0a0a0] px-2">{(d.images || []).length} slides</span>
        </div>
      )}
      {d.published?.postId && <p className="text-[11px] text-blue-600 mb-2" data-testid="draft-published-id">Published · {d.published.platform} id {d.published.postId}</p>}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => onCopy(d.content)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors"><Copy size={13} /> Copy</button>
        {d.status !== "approved" && d.status !== "published" && <button onClick={() => onStatus(d.id, "approved")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#e2d8c8] text-[12px] text-green-600 hover:border-green-400 transition-colors"><Check size={13} /> Approve</button>}
        {d.status !== "rejected" && d.status !== "published" && <button onClick={() => onStatus(d.id, "rejected")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-red-400 hover:text-red-500 transition-colors"><X size={13} /> Reject</button>}
        {canPublish && renderPublish()}
        {d.type === "blog" && onPublishBlog && d.status !== "published" && (
          <button onClick={() => onPublishBlog(d)} data-testid="publish-blog-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#b8935f] text-white text-[12px] hover:bg-[#a67f4d] transition-colors"><Send size={13} /> Publish to website</button>
        )}
        {canPublish && onSchedule && d.platform !== "tiktok" && d.status !== "scheduled" && (
          <button onClick={() => onSchedule(d)} data-testid="schedule-best-time-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#b8935f] text-[12px] text-[#b8935f] hover:bg-[#b8935f] hover:text-white transition-colors"><Clock size={13} /> Schedule best time</button>
        )}
        {d.status === "scheduled" && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] text-purple-600"><Clock size={13} /> Scheduled</span>}
        <button onClick={() => onDelete(d.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#e2d8c8] text-[12px] text-red-500 hover:border-red-400 transition-colors"><Trash2 size={13} /></button>
      </div>
    </div>

  );
};

const MKT_GROUPS = [
  { key: "create", label: "Content & Campaigns", icon: Sparkles, tabs: [["command", "AI Command", Sparkles], ["studio", "Content Studio", ImageIcon], ["launches", "Launches", Rocket], ["blog", "Blog Writer", PenLine], ["social", "Social", Share2]] },
  { key: "publish", label: "Social & Publishing", icon: Rocket, tabs: [["calendar", "Calendar", CalendarDays], ["preview", "Feed Preview", Grid3x3], ["media", "Media", ImageIcon], ["activity", "Activity", Activity]] },
  { key: "ads", label: "Ads & Performance", icon: BarChart3, tabs: [["adsocial", "Ads & Social", Megaphone], ["analytics", "Analytics", BarChart3]] },
  { key: "outreach", label: "Outreach & Prospecting", icon: TargetIcon, tabs: [["email", "Email", Mail], ["prospects", "Prospect Vault", Radar]] },
  { key: "brand", label: "Brand & Connections", icon: Palette, tabs: [["kit", "Content Kit", Palette], ["bio", "Link in Bio", Link2], ["connections", "Connections", Rocket]] },
];


const Marketing = () => {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const [drafts, setDrafts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [kit, setKit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [properties, setProperties] = useState([]);

  const isAdmin = user && (user.role === "admin" || user.role === "agent");
  const [mktGroup, setMktGroup] = useState("create");
  const [mktTab, setMktTab] = useState("command");
  const curMktGroup = MKT_GROUPS.find((g) => g.key === mktGroup) || MKT_GROUPS[0];

  const refreshDrafts = useCallback(() => getDrafts().then((ds) => {
    const seen = new Set();
    setDrafts((ds || []).filter((d) => d && d.id && !seen.has(d.id) && seen.add(d.id)));
  }).catch(() => {}), []);
  useEffect(() => {
    if (isAdmin) {
      refreshDrafts();
      getCampaigns().then(setCampaigns).catch(() => {});
      getProspects().then(setProspects).catch(() => {});
      getKit().then(setKit).catch(() => {});
      fetchProperties().then((d) => setProperties(Array.isArray(d) ? d : (d.items || d.properties || []))).catch(() => {});
    }
  }, [isAdmin, refreshDrafts]);

  const copy = (t) => { navigator.clipboard?.writeText(t); toast({ title: "Copied to clipboard" }); };
  const changeStatus = async (id, status) => { await setDraftStatus(id, status); setDrafts((ds) => ds.map((d) => d.id === id ? { ...d, status } : d)); toast({ title: `Marked ${status}` }); };
  const removeDraft = async (id) => { await deleteDraft(id); setDrafts((ds) => ds.filter((d) => d.id !== id)); };

  const generate = async (payload) => {
    setBusy(true);
    try { await generateContent(payload); toast({ title: "Draft generated", description: "Saved to Pending Review." }); refreshDrafts(); return true; }
    catch { toast({ title: "Generation failed", description: "Please try again." }); return false; }
    finally { setBusy(false); }
  };

  const publishPost = async (d, channel) => {
    toast({ title: `Publishing to ${channel === "both" ? "Facebook + Instagram" : channel}…` });
    try {
      const r = await publishDraft(d.id, channel, d.image || null);
      setDrafts((ds) => ds.map((x) => x.id === d.id ? { ...x, status: "published", published: { platform: r.platform, postId: r.postId } } : x));
      const ids = (r.results || [r]).map((x) => `${x.platform}: ${x.postId}`).join(" · ");
      toast({ title: `Published live`, description: ids });
    } catch (err) {
      toast({ title: "Publish failed", description: err?.response?.data?.detail || "Check the Meta token/permissions." });
    }
  };

  const scheduleAtBest = async (d) => {
    try {
      const r = await scheduleDraft(d.id);
      setDrafts((ds) => ds.map((x) => x.id === d.id ? { ...x, status: "scheduled", scheduledFor: r.scheduledFor } : x));
      const when = new Date(r.scheduledFor).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
      toast({ title: "Scheduled at best time", description: `Goes live ${when} (enable Auto-Publish in Grow → Automation).` });
    } catch (err) {
      toast({ title: "Couldn't schedule", description: err?.response?.data?.detail || "Please try again." });
    }
  };


  const publishBlogToSite = async (d) => {
    try {
      await publishBlogFromDraft(d.id);
      setDrafts((ds) => ds.map((x) => x.id === d.id ? { ...x, status: "published" } : x));
      toast({ title: "Published to website", description: "It's now live on your public blog." });
    } catch (err) {
      toast({ title: "Couldn't publish", description: err?.response?.data?.detail || "Please try again." });
    }
  };


  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-[#8a8a8a] font-display text-[20px]">Loading...</div>;
  if (!isAdmin) return <div className="min-h-[60vh] flex flex-col items-center justify-center px-5 text-center"><h1 className="font-display text-[30px] text-[#2b2b2b] mb-3">Team access only</h1><p className="text-[14px] text-[#8a8a8a]">Sign in with an agent or admin account.</p></div>;

  return (
    <div className="min-h-[70vh] bg-[#faf7f2]">
      <div className="bg-[#1c1c1e] py-14">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <p className="text-[12px] tracking-[0.28em] uppercase text-[#d9b98a] mb-2 flex items-center gap-2"><Sparkles size={15} /> AI + Marketing</p>
          <h1 className="font-display text-white text-[34px] md:text-[44px] font-300">Marketing Suite</h1>
          <p className="text-white/50 text-[13px] mt-2">Every AI draft is saved to Pending Review — nothing publishes without your approval.</p>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-12">
        <Tabs value={mktTab} onValueChange={setMktTab}>
          <div className="mb-8">
            <div className="flex flex-wrap gap-2 mb-3" data-testid="marketing-group-nav">
              {MKT_GROUPS.map((g) => (
                <button key={g.key} onClick={() => { setMktGroup(g.key); setMktTab(g.tabs[0][0]); }} data-testid={`mkt-group-${g.key}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-500 transition-colors ${mktGroup === g.key ? "bg-[#1c1c1e] text-white" : "bg-white border border-[#f0ece5] text-[#6a6a6a] hover:border-[#b8935f]"}`}>
                  <g.icon size={15} /> {g.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5 bg-white border border-[#f0ece5] rounded-xl p-1.5" data-testid="marketing-subtabs">
              {curMktGroup.tabs.map(([v, l, Icon]) => (
                <button key={v} onClick={() => setMktTab(v)} data-testid={`mkt-tab-${v}`}
                  className={`px-4 py-2 rounded-lg text-[13px] inline-flex items-center gap-1.5 transition-colors ${mktTab === v ? "bg-[#b8935f] text-white" : "text-[#6a6a6a] hover:bg-[#faf7f2]"}`}>
                  <Icon size={14} /> {l}
                </button>
              ))}
            </div>
          </div>

          {/* AI COMMAND */}
          <TabsContent value="command">
            <CommandPanel busy={busy} onGenerate={generate} properties={properties} />
            <DraftsSection title="Recent Drafts — Pending Review" drafts={drafts.filter(d => ["post", "ad", "email", "answer"].includes(d.type))} onStatus={changeStatus} onDelete={removeDraft} onCopy={copy} onPublish={publishPost} onSchedule={scheduleAtBest} />
          </TabsContent>

          {/* CONTENT STUDIO */}
          <TabsContent value="studio">
            <ContentStudio properties={properties} onSaved={refreshDrafts} />
            <DraftsSection title="Studio Drafts — Pending Review" drafts={drafts.filter(d => d.campaign === "content-studio")} onStatus={changeStatus} onDelete={removeDraft} onCopy={copy} onPublish={publishPost} onSchedule={scheduleAtBest} />
          </TabsContent>

          {/* MULTI-PLATFORM GENERATOR */}
          <TabsContent value="multi">
            <MultiPlatform properties={properties} onSaved={refreshDrafts} />
            <DraftsSection title="Multi-Platform Drafts — Pending Review" drafts={drafts.filter(d => d.campaign === "multi-platform")} onStatus={changeStatus} onDelete={removeDraft} onCopy={copy} onPublish={publishPost} onSchedule={scheduleAtBest} />
          </TabsContent>

          {/* LAUNCH CAMPAIGNS */}
          <TabsContent value="launches">
            <LaunchCampaigns properties={properties} onSaved={refreshDrafts} />
            <DraftsSection title="Launch Drafts — Pending Review" drafts={drafts.filter(d => d.campaign === "launch")} onStatus={changeStatus} onDelete={removeDraft} onCopy={copy} onPublish={publishPost} onSchedule={scheduleAtBest} />
          </TabsContent>

          {/* CONTENT CALENDAR */}
          <TabsContent value="calendar"><ContentCalendar /></TabsContent>


          {/* BLOG */}
          <TabsContent value="blog">
            <SimpleGen label="Blog topic" placeholder="e.g. A first-time buyer's guide to buying in Bath" type="blog" busy={busy} onGenerate={generate} icon={PenLine} title="AI Blog Writer" />
            <DraftsSection title="Blog Drafts" drafts={drafts.filter(d => d.type === "blog")} onStatus={changeStatus} onDelete={removeDraft} onCopy={copy} onPublishBlog={publishBlogToSite} />
          </TabsContent>

          {/* SOCIAL */}
          <TabsContent value="social">
            <SocialPanel busy={busy} onGenerate={generate} />
            <DraftsSection title="Social Drafts" drafts={drafts.filter(d => ["post", "caption", "carousel"].includes(d.type) && d.platform)} onStatus={changeStatus} onDelete={removeDraft} onCopy={copy} onPublish={publishPost} onSchedule={scheduleAtBest} />
          </TabsContent>

          <TabsContent value="adsocial"><GrowthAds /></TabsContent>
          <TabsContent value="ads"><AdsPanel toast={toast} /></TabsContent>
          <TabsContent value="preview"><SocialPreview /></TabsContent>
          <TabsContent value="analytics"><SocialAnalytics /></TabsContent>
          <TabsContent value="activity"><ActivityFeed /></TabsContent>
          <TabsContent value="scheduler"><SchedulerPanel toast={toast} /></TabsContent>
          <TabsContent value="media"><MediaPanel toast={toast} /></TabsContent>

          {/* EMAIL */}
          <TabsContent value="email">
            <EmailPanel busy={busy} onGenerate={generate} campaigns={campaigns} onCreate={async (p) => { const c = await createCampaign(p); setCampaigns((cs) => [c, ...cs]); toast({ title: p.scheduledFor ? "Campaign scheduled" : "Campaign saved as draft" }); }} />
          </TabsContent>

          {/* PROSPECTS */}
          <TabsContent value="prospects">
            <ProspectPanel prospects={prospects} setProspects={setProspects} toast={toast} />
          </TabsContent>

          {/* CONTENT KIT */}
          <TabsContent value="kit">
            {kit && <KitPanel kit={kit} onSave={async (k) => { const saved = await updateKit(k); setKit(saved); toast({ title: "Content kit saved" }); }} />}
          </TabsContent>
          <TabsContent value="bio"><BioManager /></TabsContent>
          <TabsContent value="connections"><Connections /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const DraftsSection = ({ title, drafts, ...h }) => (
  <div className="mt-8">
    <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] mb-4">{title} ({drafts.length})</h3>
    {drafts.length === 0 ? <p className="text-[14px] text-[#8a8a8a]">No drafts yet — generate one above.</p> :
      <div className="grid md:grid-cols-2 gap-5">{drafts.map((d) => <DraftCard key={d.id} d={d} {...h} />)}</div>}
  </div>
);

const CommandPanel = ({ busy, onGenerate, properties = [] }) => {
  const [type, setType] = useState("post");
  const [prompt, setPrompt] = useState("");
  const [tone, setTone] = useState("");
  const [image, setImage] = useState(null);
  const [imageName, setImageName] = useState("");
  const [propertyId, setPropertyId] = useState("none");
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setImage(reader.result); setImageName(f.name); };
    reader.readAsDataURL(f);
  };
  const submit = async (e) => { e.preventDefault(); if (!prompt.trim()) return; const ok = await onGenerate({ type, prompt, tone, imageBase64: image, propertyId: propertyId !== "none" ? propertyId : null }); if (ok) { setPrompt(""); setImage(null); setImageName(""); } };
  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
      <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] mb-1 flex items-center gap-2"><Sparkles size={18} className="text-[#b8935f]" /> AI Command</h3>
      <p className="text-[13px] text-[#8a8a8a] mb-5">One prompt → a Post, Ad, Email or Answer. Pick a live listing to auto-pull its real details, or attach a photo. Saved to Pending Review.</p>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-[46px] border-[#e2d8c8]"><SelectValue /></SelectTrigger>
            <SelectContent>{[["post", "Social Post"], ["ad", "Advert"], ["email", "Email"], ["answer", "Client Answer"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <input value={tone} onChange={(e) => setTone(e.target.value)} placeholder="Tone (optional, e.g. upbeat)" className={input} />
        </div>
        <Select value={propertyId} onValueChange={setPropertyId}>
          <SelectTrigger className="h-[46px] border-[#e2d8c8]" data-testid="command-property-select"><SelectValue placeholder="Link a listing (optional)" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No listing — freeform</SelectItem>
            {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.title} — £{Number(p.price || 0).toLocaleString()}</SelectItem>)}
          </SelectContent>
        </Select>
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} placeholder="What should this be about? e.g. Promote our new 3-bed home on Duke Street" className={area} />
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#e2d8c8] text-[13px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors cursor-pointer" data-testid="ai-photo-label">
            <ImageIcon size={15} /> {imageName ? "Change photo" : "Attach photo"}
            <input type="file" accept="image/*" hidden onChange={onFile} data-testid="ai-photo-input" />
          </label>
          {imageName && <span className="text-[12px] text-[#8a8a8a] flex items-center gap-2"><img src={image} alt="" className="w-8 h-8 rounded object-cover" /> {imageName} <button type="button" onClick={() => { setImage(null); setImageName(""); }} className="text-red-500"><X size={13} /></button></span>}
        </div>
        <button type="submit" disabled={busy} className={btn}>{busy ? <><Loader2 size={16} className="animate-spin" /> Generating...</> : <><Zap size={16} /> Generate Draft</>}</button>
      </form>
    </div>
  );
};

const SimpleGen = ({ label, placeholder, type, busy, onGenerate, icon: Icon, title }) => {
  const [prompt, setPrompt] = useState("");
  const submit = async (e) => { e.preventDefault(); if (!prompt.trim()) return; const ok = await onGenerate({ type, prompt }); if (ok) setPrompt(""); };
  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
      <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] mb-4 flex items-center gap-2"><Icon size={18} className="text-[#b8935f]" /> {title}</h3>
      <form onSubmit={submit} className="space-y-3">
        <label className="text-[13px] text-[#6a6a6a]">{label}</label>
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2} placeholder={placeholder} className={area} />
        <button type="submit" disabled={busy} className={btn}>{busy ? <><Loader2 size={16} className="animate-spin" /> Writing...</> : <><Zap size={16} /> Generate</>}</button>
      </form>
    </div>
  );
};

const SocialPanel = ({ busy, onGenerate }) => {
  const [platform, setPlatform] = useState("instagram");
  const [type, setType] = useState("post");
  const [prompt, setPrompt] = useState("");
  const submit = async (e) => { e.preventDefault(); if (!prompt.trim()) return; const ok = await onGenerate({ type, prompt, platform }); if (ok) setPrompt(""); };
  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
      <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] mb-1 flex items-center gap-2"><Share2 size={18} className="text-[#b8935f]" /> Social & Captions</h3>
      <p className="text-[13px] text-[#8a8a8a] mb-5">Generate on-brand captions & posts. Approve one, then publish it live to Facebook or Instagram from Pending Review.</p>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <Select value={platform} onValueChange={setPlatform}><SelectTrigger className="h-[46px] border-[#e2d8c8]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="instagram">Instagram</SelectItem><SelectItem value="facebook">Facebook</SelectItem></SelectContent></Select>
          <Select value={type} onValueChange={setType}><SelectTrigger className="h-[46px] border-[#e2d8c8]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="post">Post</SelectItem><SelectItem value="caption">Caption</SelectItem></SelectContent></Select>
        </div>
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2} placeholder="e.g. New listing: Royal Crescent apartment, open house Saturday" className={area} />
        <button type="submit" disabled={busy} className={btn}>{busy ? <><Loader2 size={16} className="animate-spin" /> Generating...</> : <><Zap size={16} /> Generate</>}</button>
      </form>
    </div>
  );
};

const EmailPanel = ({ busy, onGenerate, campaigns, onCreate }) => {
  const [form, setForm] = useState({ name: "", subject: "", body: "", audience: "all-contacts", scheduledFor: "" });
  const submit = (e) => { e.preventDefault(); if (!form.name || !form.subject) return; onCreate({ ...form, scheduledFor: form.scheduledFor || null }); setForm({ name: "", subject: "", body: "", audience: "all-contacts", scheduledFor: "" }); };
  return (
    <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
        <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] mb-4 flex items-center gap-2"><Mail size={18} className="text-[#b8935f]" /> Email Campaign</h3>
        <form onSubmit={submit} className="space-y-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Campaign name" className={input} />
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Subject line" className={input} />
          <AiAssist channel="email" context={`Email campaign "${form.name || form.subject}" to ${form.audience}`} value={form.body} onChange={(v) => setForm({ ...form, body: v })} className="py-1" testid="email-ai-assist" />
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={5} placeholder="Email body..." className={area} />
          <div className="grid sm:grid-cols-2 gap-3">
            <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}><SelectTrigger className="h-[46px] border-[#e2d8c8]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all-contacts">All contacts</SelectItem><SelectItem value="landlords">Landlords</SelectItem><SelectItem value="buyers">Buyers</SelectItem><SelectItem value="vendors">Vendors</SelectItem></SelectContent></Select>
            <input type="date" value={form.scheduledFor} onChange={(e) => setForm({ ...form, scheduledFor: e.target.value })} className={input + " text-[#4a4a4a]"} />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => onGenerate({ type: "email", prompt: form.subject || form.name || "a friendly market update" }).then(() => {})} disabled={busy} className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-[#e2d8c8] text-[#6a6a6a] text-[14px] font-500 hover:border-[#b8935f] hover:text-[#b8935f] transition-colors"><Sparkles size={15} /> AI Draft</button>
            <button type="submit" className={btn}><Send size={15} /> {form.scheduledFor ? "Schedule" : "Save Draft"}</button>
          </div>
          <p className="text-[12px] text-[#a0a0a0]">Sending is simulated — connect Resend to send live. AI draft appears in Pending Review.</p>
        </form>
      </div>
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
        <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] mb-4">Campaign History</h3>
        <div className="space-y-2">
          {campaigns.length === 0 ? <p className="text-[14px] text-[#8a8a8a]">No campaigns yet.</p> :
            campaigns.map((c) => (
              <div key={c.id} className="p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]">
                <div className="flex items-center justify-between"><p className="text-[14px] font-500 text-[#2b2b2b]">{c.name}</p><span className={`text-[11px] px-2.5 py-1 rounded-full capitalize ${c.status === "scheduled" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>{c.status}</span></div>
                <p className="text-[12px] text-[#a0a0a0]">{c.subject} · {c.recipients} recipients · {c.audience}</p>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

const PSTAGES = ["identified", "contacted", "responded", "valuation", "won", "lost"];
const sigColor = (s) => ({ "Withdrawn": "bg-red-100 text-red-700", "Probate": "bg-purple-100 text-purple-700", "Price Reduced": "bg-amber-100 text-amber-700", "On Market (Competitor)": "bg-blue-100 text-blue-700" }[s] || "bg-[#b8935f]/10 text-[#b8935f]");

const ProspectPanel = ({ prospects, setProspects, toast }) => {
  const [roi, setRoi] = useState(null);
  const [gen, setGen] = useState({ area: "", address: "", target: "both" });
  const [generating, setGenerating] = useState(false);
  const scoreColor = (s) => s >= 80 ? "text-green-600" : s >= 60 ? "text-amber-600" : "text-[#8a8a8a]";
  const loadRoi = () => getProspectRoi().then(setRoi).catch(() => {});
  useEffect(() => { loadRoi(); }, []);
  const runGenerate = async (e) => {
    e.preventDefault();
    if (!gen.area.trim()) { toast({ title: "Enter an area or postcode" }); return; }
    setGenerating(true);
    try {
      const r = await generateProspects({ area: gen.area.trim(), address: gen.address.trim(), target: gen.target, count: 5 });
      setProspects((ps) => [...r.prospects, ...ps]);
      toast({ title: `${r.created} prospects generated`, description: `Sellers & landlords around ${gen.area}` });
      setGen({ area: "", address: "", target: "both" });
    } catch { toast({ title: "Couldn't generate prospects" }); }
    finally { setGenerating(false); }
  };
  const enrich = async (id) => { const p = await enrichProspect(id); setProspects((ps) => ps.map((x) => x.id === id ? p : x)); toast({ title: "Prospect enriched", description: "Contact & Companies House data revealed (mocked)." }); };
  const convert = async (id) => { await convertProspect(id); setProspects((ps) => ps.map((x) => x.id === id ? { ...x, converted: true } : x)); toast({ title: "Converted to lead" }); };
  const changeStage = async (id, stage) => { const p = await setProspectStage(id, stage); setProspects((ps) => ps.map((x) => x.id === id ? p : x)); loadRoi(); toast({ title: `Stage → ${stage}` }); };
  const drip = async (id) => {
    try {
      const camps = await getProspectCampaigns();
      let camp = camps.find((c) => c.name === "3-Touch Landlord Drip");
      if (!camp) camp = await createProspectCampaign({ name: "3-Touch Landlord Drip", touches: [{ day: 0, channel: "letter", note: "Intro letter" }, { day: 14, channel: "letter", note: "Follow-up + market update" }, { day: 30, channel: "email", note: "Final nudge + free valuation offer" }] });
      await enrolProspects(camp.id, [id]);
      setProspects((ps) => ps.map((x) => x.id === id ? { ...x, stage: x.stage === "identified" ? "contacted" : x.stage } : x));
      loadRoi();
      toast({ title: "Enrolled in 3-touch drip", description: "Letters & email scheduled (mocked)." });
    } catch { toast({ title: "Couldn't start campaign" }); }
  };
  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] flex items-center gap-2"><Radar size={18} className="text-[#b8935f]" /> Prospect Vault</h3>
        <button onClick={() => exportProspectsCsv()} data-testid="export-prospects-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors"><Copy size={13} /> Export CSV</button>
      </div>
      <p className="text-[13px] text-[#8a8a8a] mb-4">Spectre-style landlord & vendor prospecting with signal-weighted 0–100 scoring and multi-touch drip campaigns. Enrichment & Companies House are mocked until keys are added.</p>

      <form onSubmit={runGenerate} className="bg-[#faf7f2] rounded-xl border border-[#f0ece5] p-4 mb-6" data-testid="prospect-generator">
        <p className="text-[13px] font-500 text-[#2b2b2b] mb-2.5">Generate prospects for an area</p>
        <div className="grid sm:grid-cols-[1fr_1fr_auto_auto] gap-2">
          <input required placeholder="Area / postcode (e.g. BA2)" value={gen.area} onChange={(e) => setGen({ ...gen, area: e.target.value })} className="h-10 px-3 rounded-lg border border-[#e2d8c8] text-[13px] outline-none focus:border-[#b8935f]" data-testid="prospect-gen-area" />
          <input placeholder="Specific address (optional)" value={gen.address} onChange={(e) => setGen({ ...gen, address: e.target.value })} className="h-10 px-3 rounded-lg border border-[#e2d8c8] text-[13px] outline-none focus:border-[#b8935f]" data-testid="prospect-gen-address" />
          <select value={gen.target} onChange={(e) => setGen({ ...gen, target: e.target.value })} className="h-10 px-3 rounded-lg border border-[#e2d8c8] text-[13px] outline-none focus:border-[#b8935f]" data-testid="prospect-gen-target">
            <option value="both">Sellers + Landlords</option>
            <option value="sales">Sellers / sales</option>
            <option value="lettings">Landlords</option>
          </select>
          <button type="submit" disabled={generating} className="h-10 px-5 rounded-lg bg-[#1c1c1e] text-white text-[13px] font-500 hover:bg-black transition-colors disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="prospect-gen-btn">{generating ? <Loader2 size={14} className="animate-spin" /> : <Radar size={14} />} Generate</button>
        </div>
      </form>

      {roi && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6" data-testid="prospect-roi">
          {[["Identified", roi.total], ["Contacted", roi.contacted], ["Responded", roi.responded], ["Valuations", roi.valuations], ["Won", roi.won]].map(([l, v], i) => (
            <div key={l} className="bg-[#faf7f2] rounded-xl border border-[#f0ece5] p-3 text-center">
              <p className="font-display text-[22px] font-500 text-[#2b2b2b]">{v}</p>
              <p className="text-[10px] text-[#a0a0a0] uppercase tracking-wide">{l}</p>
            </div>
          ))}
        </div>
      )}
      {roi && <p className="text-[12px] text-[#8a8a8a] mb-5">Response rate <span className="text-[#b8935f] font-500">{roi.responseRate}%</span> · Conversion <span className="text-[#b8935f] font-500">{roi.conversionRate}%</span> · Est. pipeline value <span className="text-green-600 font-500">£{roi.estRevenue.toLocaleString()}</span> · Touches scheduled {roi.touchesSent}</p>}

      <div className="space-y-2">
        {prospects.map((p) => (
          <div key={p.id} className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 p-4 rounded-lg bg-[#faf7f2] border border-[#f0ece5]" data-testid="prospect-row">
            <div className="flex items-center gap-4">
              <div className="text-center w-14"><p className={`font-display text-[24px] font-500 ${scoreColor(p.score)}`}>{p.score}</p><p className="text-[10px] text-[#a0a0a0] uppercase">score</p></div>
              <div>
                <p className="text-[15px] font-500 text-[#2b2b2b]">{p.owner} <span className="text-[12px] text-[#a0a0a0]">· {p.company}</span></p>
                <p className="text-[13px] text-[#8a8a8a]">{p.address}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${sigColor(p.signal)}`}>{p.signal}</span>
                  <span className="text-[11px] text-[#a0a0a0]">{p.portfolio} properties</span>
                  {p.competitor && p.competitor !== "—" && <span className="text-[11px] text-[#a0a0a0]">· vs {p.competitor}</span>}
                  {p.daysOnMarket > 0 && <span className="text-[11px] text-[#a0a0a0]">· {p.daysOnMarket}d on market</span>}
                  {p.enriched && p.email && <span className="text-[11px] text-[#6a6a6a]">· {p.email}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={p.stage || "identified"} onValueChange={(v) => changeStage(p.id, v)}>
                <SelectTrigger className="w-[130px] h-9 border-[#e2d8c8]" data-testid="prospect-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PSTAGES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
              <button onClick={() => drip(p.id)} data-testid="drip-btn" title="Start 3-touch drip" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors"><Send size={14} /> Drip</button>
              {!p.enriched && <button onClick={() => enrich(p.id)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors"><TrendingUp size={14} /> Enrich</button>}
              <button onClick={() => downloadProspectLetter(p.id)} data-testid="prospect-letter-btn" title="Generate letter PDF" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors"><Mail size={14} /> Letter</button>
              {p.converted ? <span className="text-[12px] text-green-600 flex items-center gap-1"><Check size={14} /> Lead</span> :
                <button onClick={() => convert(p.id)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#b8935f] text-white text-[12px] font-500 hover:bg-[#a67f4d] transition-colors"><UserPlus size={14} /> Convert</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const KitPanel = ({ kit, onSave }) => {
  const [form, setForm] = useState(kit);
  const fields = [["brandName", "Brand name"], ["voice", "Brand voice"], ["areas", "Areas covered"], ["usp", "Unique selling point"], ["signature", "Sign-off"]];
  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-6 max-w-[640px]">
      <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] mb-1 flex items-center gap-2"><Palette size={18} className="text-[#b8935f]" /> Content Kit</h3>
      <p className="text-[13px] text-[#8a8a8a] mb-5">Your brand settings steer every AI generation across the suite.</p>
      <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-3">
        {fields.map(([k, label]) => (
          <div key={k}><label className="text-[13px] text-[#6a6a6a]">{label}</label><input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={input} /></div>
        ))}
        <button type="submit" className={btn}>Save Content Kit</button>
      </form>
    </div>
  );
};

const AdsPanel = ({ toast }) => {
  const [ads, setAds] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({ platform: "meta", objective: "traffic", headline: "", primaryText: "", budget: 20 });
  useEffect(() => { getAds().then(setAds).catch(() => {}); import("../api").then(m => m.getAdTemplates().then(setTemplates).catch(() => {})); }, []);
  const submit = async (e) => { e.preventDefault(); if (!form.headline) return; const ad = await createAd(form); setAds((a) => [ad, ...a]); toast({ title: "Ad draft created", description: "Saved to Pending Review." }); setForm({ ...form, headline: "", primaryText: "" }); };
  const approve = async (id, status) => { await setAdStatus(id, status); setAds((a) => a.map((x) => x.id === id ? { ...x, status } : x)); toast({ title: `Ad ${status}` }); };
  return (
    <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
        <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] mb-1 flex items-center gap-2"><Megaphone size={18} className="text-[#b8935f]" /> Ads Hub</h3>
        <p className="text-[13px] text-[#8a8a8a] mb-4">Build Meta/Google ad drafts. Publishing is mocked until keys are connected.</p>
        {templates.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {templates.map((t) => <button key={t.id} onClick={() => setForm({ ...form, headline: t.headline, primaryText: t.primaryText })} className="text-[11px] px-3 py-1.5 rounded-full bg-[#faf7f2] border border-[#e2d8c8] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors">{t.name}</button>)}
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}><SelectTrigger className="h-[46px] border-[#e2d8c8]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="meta">Meta (FB/IG)</SelectItem><SelectItem value="google">Google Ads</SelectItem></SelectContent></Select>
            <input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: Number(e.target.value) })} placeholder="Daily budget £" className={input} />
          </div>
          <input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="Headline" className={input} />
          <AiAssist channel="ad" context={`${form.platform} advert, headline: ${form.headline}, £${form.budget}/day`} value={form.primaryText} onChange={(v) => setForm({ ...form, primaryText: v })} className="py-1" testid="ad-ai-assist" />
          <textarea value={form.primaryText} onChange={(e) => setForm({ ...form, primaryText: e.target.value })} rows={3} placeholder="Primary text" className={area} />
          <button type="submit" className={btn}><Zap size={15} /> Create Ad Draft</button>
        </form>
      </div>
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
        <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] mb-4">Ad Drafts ({ads.length})</h3>
        <div className="space-y-2 max-h-[480px] overflow-y-auto">
          {ads.length === 0 ? <p className="text-[14px] text-[#8a8a8a]">No ad drafts yet.</p> : ads.map((a) => (
            <div key={a.id} className="p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]">
              <div className="flex items-center justify-between"><p className="text-[14px] font-500 text-[#2b2b2b]">{a.headline}</p><span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${a.status === "approved" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{a.status === "pending" ? "Pending" : a.status}</span></div>
              <p className="text-[12px] text-[#8a8a8a] mt-1">{a.platform} · £{a.budget}/day · {a.primaryText}</p>
              {a.status !== "approved" && <button onClick={() => approve(a.id, "approved")} className="mt-2 text-[12px] text-green-600 inline-flex items-center gap-1"><Check size={13} /> Approve</button>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SchedulerPanel = ({ toast }) => {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({ kind: "post", platform: "instagram", caption: "", scheduledFor: "" });
  useEffect(() => { getScheduledPosts().then(setPosts).catch(() => {}); }, []);
  const submit = async (e) => { e.preventDefault(); if (!form.caption) return; const p = await schedulePost({ ...form, scheduledFor: form.scheduledFor || null }); setPosts((ps) => [p, ...ps]); toast({ title: form.scheduledFor ? "Post scheduled" : "Post queued", description: form.scheduledFor ? "It will go live at the time you set (enable Auto-Publish in Growth → Automation)." : "Saved to Pending Review — nothing auto-posts." }); setForm({ ...form, caption: "", scheduledFor: "" }); };
  const fmtWhen = (iso) => { if (!iso) return null; try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  return (
    <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
        <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] mb-1 flex items-center gap-2"><CalendarClock size={18} className="text-[#b8935f]" /> Post & Story Scheduler</h3>
        <p className="text-[13px] text-[#8a8a8a] mb-4">Pick a date & time to send your Instagram / Facebook posts out. With Auto-Publish on, they go live automatically at that moment.</p>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}><SelectTrigger className="h-[46px] border-[#e2d8c8]"><SelectValue /></SelectTrigger><SelectContent>{["post", "story", "reel", "carousel"].map((k) => <SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}</SelectContent></Select>
            <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}><SelectTrigger className="h-[46px] border-[#e2d8c8]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="instagram">Instagram</SelectItem><SelectItem value="facebook">Facebook</SelectItem></SelectContent></Select>
          </div>
          <AiAssist channel="social" context={`${form.kind} for ${form.platform}`} value={form.caption} onChange={(v) => setForm({ ...form, caption: v })} className="py-1" testid="scheduler-ai-assist" />
          <textarea value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} rows={3} placeholder="Caption..." className={area} data-testid="scheduler-caption" />
          <div>
            <label className="text-[12px] text-[#6a6a6a] mb-1 block">Send date & time</label>
            <input type="datetime-local" value={form.scheduledFor} onChange={(e) => setForm({ ...form, scheduledFor: e.target.value })} className={input + " text-[#4a4a4a]"} data-testid="scheduler-datetime" />
          </div>
          <button type="submit" className={btn} data-testid="scheduler-submit"><Send size={15} /> {form.scheduledFor ? "Schedule Post" : "Queue Post"}</button>
        </form>
      </div>
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
        <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] mb-4">Scheduled Queue ({posts.length})</h3>
        <div className="space-y-2 max-h-[480px] overflow-y-auto">
          {posts.length === 0 ? <p className="text-[14px] text-[#8a8a8a]">Nothing queued yet.</p> : posts.map((p) => (
            <div key={p.id} className="p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]">
              <div className="flex items-center justify-between"><p className="text-[13px] font-500 text-[#2b2b2b] capitalize">{p.kind} · {p.platform}</p><span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${p.status === "published" ? "bg-blue-100 text-blue-700" : p.status === "failed" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{p.status || "pending"}</span></div>
              <p className="text-[12px] text-[#6a6a6a] mt-1">{p.caption}</p>
              <p className="text-[11px] text-[#a0a0a0] mt-1">{fmtWhen(p.scheduledFor) ? `Sends: ${fmtWhen(p.scheduledFor)}` : `Best time: ${p.bestTime}`}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MediaPanel = ({ toast }) => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const load = (query) => getMedia(query).then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
      <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] mb-1 flex items-center gap-2"><ImageIcon size={18} className="text-[#b8935f]" /> Media Library</h3>
      <p className="text-[13px] text-[#8a8a8a] mb-4">Your uploaded media library. Search filters by tag.</p>
      <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-2 mb-5">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Unsplash (e.g. kitchen)" className={input} />
        <button type="submit" className={btn}>Search</button>
      </form>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {items.map((m) => (
          <div key={m.id} className="rounded-lg overflow-hidden border border-[#f0ece5] relative group">
            <img src={m.url} alt={m.tag} className="w-full h-28 object-cover" />
            <span className="absolute bottom-1 left-1 text-[10px] px-1.5 py-0.5 rounded bg-black/50 text-white">{m.source}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Marketing;
